import { useCallback, useEffect, useState } from 'react';
import { fetchBillingSummary, verifyInvoicePayment } from '../api/client';
import { loadPaystackScript } from '../utils/loadPaystack';
import { downloadInvoicePdf } from '../utils/generateInvoicePdf';
import CheckoutModal from './CheckoutModal';
import './BillingPanel.css';

function DownloadIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
      <path
        d="M12 3V15M12 15L7 10M12 15L17 10M5 19H19"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function formatKES(amount) {
  return `KES ${Number(amount || 0).toLocaleString()}`;
}

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export default function BillingPanel({ user }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [payingId, setPayingId] = useState(null);
  const [payError, setPayError] = useState('');
  const [checkoutInvoice, setCheckoutInvoice] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchBillingSummary();
      setSummary(data);
    } catch (err) {
      setError(err.message || 'Failed to load billing details');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function handlePay(invoice) {
    setPayError('');
    setCheckoutInvoice(null);
    setPayingId(invoice.id);
    try {
      const PaystackPop = await loadPaystackScript();
      const reference = `piua_inv${invoice.id}_${Date.now()}`;

      const handler = PaystackPop.setup({
        key: process.env.REACT_APP_PAYSTACK_PUBLIC_KEY,
        email: user.email,
        amount: invoice.chargeAmount * 100,
        currency: invoice.currency,
        ref: reference,
        metadata: { invoiceId: invoice.id },
        callback: (response) => {
          verifyInvoicePayment(invoice.id, response.reference)
            .then(() => load())
            .catch((err) => setPayError(err.message || 'Payment could not be verified'))
            .finally(() => setPayingId(null));
        },
        onClose: () => setPayingId(null),
      });

      handler.openIframe();
    } catch (err) {
      setPayError(err.message || 'Unable to start payment');
      setPayingId(null);
    }
  }

  if (loading) return <div className="billing-loading">Loading billing details…</div>;
  if (error) return <div className="billing-error">{error}</div>;

  return (
    <>
      <div className="billing-summary-grid">
        <div className="billing-stat">
          <span className="billing-stat-label">Next billing cycle</span>
          <span className="billing-stat-value">
            {summary.daysToNextBilling != null ? `${summary.daysToNextBilling} days` : '—'}
          </span>
          <span className="billing-stat-sub">{formatDate(summary.nextBillingDate)}</span>
        </div>
        <div className="billing-stat">
          <span className="billing-stat-label">Amount due</span>
          <span className="billing-stat-value">{formatKES(summary.amountDue)}</span>
          <span className="billing-stat-sub">
            {summary.dueInvoices.length} due invoice{summary.dueInvoices.length === 1 ? '' : 's'}
          </span>
        </div>
      </div>

      {payError && <div className="billing-error">{payError}</div>}

      {summary.dueInvoices.length > 0 && (
        <div className="billing-section">
          <h3>Due invoices</h3>
          <div className="billing-invoice-list">
            {summary.dueInvoices.map((inv) => (
              <div key={inv.id} className="billing-invoice-row">
                <div>
                  <div className="billing-invoice-period">
                    {formatDate(inv.periodStart)} – {formatDate(inv.periodEnd)}
                  </div>
                  <div className="billing-invoice-amount">{formatKES(inv.amount)}</div>
                </div>
                <div className="billing-invoice-actions">
                  <button
                    className="billing-icon-btn"
                    onClick={() => downloadInvoicePdf(inv, user)}
                    title="Download invoice"
                  >
                    <DownloadIcon />
                  </button>
                  <button
                    className="billing-pay-btn"
                    onClick={() => setCheckoutInvoice(inv)}
                    disabled={payingId === inv.id}
                  >
                    {payingId === inv.id ? 'Processing…' : 'Pay'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="billing-section">
        <h3>Billing statement</h3>
        <div className="billing-history-table">
          <div className="billing-history-head">
            <span>Period</span>
            <span>Amount</span>
            <span>Status</span>
            <span></span>
          </div>
          {summary.history.map((inv) => (
            <div key={inv.id} className="billing-history-row">
              <span>{formatDate(inv.periodEnd)}</span>
              <span>{formatKES(inv.amount)}</span>
              <span className={`billing-status billing-status--${inv.status}`}>{inv.status}</span>
              <span className="billing-history-action">
                <button
                  className="billing-icon-btn"
                  onClick={() => downloadInvoicePdf(inv, user)}
                  title="Download invoice"
                >
                  <DownloadIcon />
                </button>
              </span>
            </div>
          ))}
        </div>
      </div>

      {checkoutInvoice && (
        <CheckoutModal
          title="Checkout"
          items={[
            {
              label: `${user?.packageName || 'Piua AI'} subscription — ${formatDate(
                checkoutInvoice.periodStart
              )} – ${formatDate(checkoutInvoice.periodEnd)}`,
              amount: checkoutInvoice.amount,
            },
          ]}
          currency={checkoutInvoice.currency}
          processing={payingId === checkoutInvoice.id}
          error={payError}
          onDownloadInvoice={() => downloadInvoicePdf(checkoutInvoice, user)}
          onCheckout={() => handlePay(checkoutInvoice)}
          onClose={() => setCheckoutInvoice(null)}
        />
      )}
    </>
  );
}
